import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Image as ImageIcon, Video, Calendar, Bot, Download, FlaskConical } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { handlePostUpdate } from '../lib/algoliaSync';
import ImageSelectorModal from './ImageSelectorModal';
import LabExerciseSelectorModal from './LabExerciseSelectorModal';
import VideoSelectorModal from './VideoSelectorModal';
import BlogAIModal from './BlogAIModal';
import DownloadSelectorModal from './DownloadSelectorModal';

const INITIAL_FORM_STATE = {
  title: '',
  slug: '',
  content: '',
  excerpt: '',
  image_url: '',
  author_id: '',
  category_id: '', 
  lab_exercise_id: null,
  is_published: false,
  published_at: '',
  cta_type: null as 'ai_training' | 'agent_dev' | 'fractional_pm' | null
};

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

function generateSlug(title: string): string {
  const wordsToRemove = ['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'];
  
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .split(/\s+/)
    .filter(word => !wordsToRemove.includes(word))
    .join('-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/^blog\//, '');
}

function generateExcerpt(content: string): string {
  const marketingPhrases = [
    'in today',
    'rapidly',
    'we\'re the #1',
    'top rated',
    'affordable',
    'cheap',
    'we offer',
    'wide range',
    'ever changing',
    'we are the best',
    'landscape',
    'click here',
    'learn more',
    'revolutionary',
    'innovative',
    'cutting-edge',
    'state-of-the-art',
    'world-class',
    'industry-leading',
    'next-generation',
    'game-changing',
    'best-in-class',
    'leading provider',
    'unparalleled',
    'unique',
    'seamless',
    'robust',
    'powerful',
    'dynamic',
    'flexible',
    'scalable',
    'comprehensive'
  ];

  let textContent = content.replace(/<[^>]+>/g, ' ');
  textContent = textContent.replace(/\s+/g, ' ').trim();
  const marketingRegex = new RegExp(marketingPhrases.join('|'), 'gi');
  textContent = textContent.replace(marketingRegex, '');
  textContent = textContent.replace(/\s+/g, ' ').trim();

  let excerpt = textContent.substring(0, 160);
  
  const lastPeriod = excerpt.lastIndexOf('.');
  const lastQuestion = excerpt.lastIndexOf('?');
  const lastExclamation = excerpt.lastIndexOf('!');
  const lastSentenceEnd = Math.max(lastPeriod, lastQuestion, lastExclamation);
  
  if (lastSentenceEnd > 50) {
    excerpt = excerpt.substring(0, lastSentenceEnd + 1);
  } else {
    const lastSpace = excerpt.lastIndexOf(' ');
    if (lastSpace > 0) {
      excerpt = excerpt.substring(0, lastSpace);
    }
    if (textContent.length > excerpt.length) {
      excerpt += '...';
    }
  }

  return excerpt
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,!?])/g, '$1')
    .replace(/([.,!?])([^\s])/g, '$1 $2')
    .trim();
}

export default function AdminPostEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authors, setAuthors] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [ctaTypes, setCtaTypes] = useState<string[]>([]);
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [showVideoSelector, setShowVideoSelector] = useState(false);
  const [showDownloadSelector, setShowDownloadSelector] = useState(false);
  const [showLabExerciseSelector, setShowLabExerciseSelector] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [contentCursorPosition, setContentCursorPosition] = useState<number | null>(null);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [labExercises, setLabExercises] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const retryCount = useRef(0);

  useEffect(() => {
    fetchData();
  }, [id]);

  async function fetchData() {
    try {
      setLoading(true);
      setError(null);

      // Fetch lab exercises
      const { data: labExercisesData, error: labExercisesError } = await supabase
        .from('lab_exercises')
        .select('id, title')
        .eq('is_active', true)
        .order('order_number');

      if (labExercisesError) {
        console.error('Error fetching lab exercises:', labExercisesError);
      } else {
        setLabExercises(labExercisesData || []);
      }

      // Fetch CTA types
      const { data: ctaTypesData, error: ctaTypesError } = await supabase
        .from('ctas')
        .select('type')
        .eq('is_active', true);

      if (ctaTypesError) {
        // Fallback to hardcoded types if query fails
        setCtaTypes(['ai_training', 'agent_dev', 'fractional_pm']);
      } else {
        const uniqueTypes = Array.from(new Set(ctaTypesData.map(row => row.type)));
        setCtaTypes(uniqueTypes);
      }

      // Fetch authors and categories in parallel
      const [authorsRes, categoriesRes] = await Promise.all([
        supabase.from('blog_authors').select('*').order('name'),
        supabase.from('blog_categories').select('*').order('name')
      ]);

      if (authorsRes.error) throw authorsRes.error;
      if (categoriesRes.error) throw categoriesRes.error;

      setAuthors(authorsRes.data || []);
      setCategories(categoriesRes.data || []);

      if (id) {
        const { data: post, error: postError } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('id', id)
          .single();

        if (postError) throw postError;
        if (post) {
          const formattedDate = post.published_at ? new Date(post.published_at).toISOString().split('T')[0] : '';
          setFormData({
            ...INITIAL_FORM_STATE,
            ...post,
            published_at: formattedDate,
            meta_title: post.meta_title || '',
            meta_description: post.meta_description || '',
            meta_keywords: post.meta_keywords || [],
            canonical_url: post.canonical_url || '',
            lab_exercise_id: post.lab_exercise_id || null
          });
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
      
      if (retryCount.current < MAX_RETRIES) {
        retryCount.current++;
        setTimeout(fetchData, RETRY_DELAY);
      }
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const submissionData = {
        ...formData,
        published_at: formData.published_at || null,
        updated_at: new Date().toISOString()
      };
      
      let result;
      
      if (id) {
        const { data, error } = await supabase
          .from('blog_posts')
          .update(submissionData)
          .eq('id', id)
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        const { data, error } = await supabase
          .from('blog_posts')
          .insert([{
            ...submissionData,
            created_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (error) throw error;
        result = data;
      }

      if (result && formData.is_published) {
        try {
          await handlePostUpdate(result);
        } catch (indexError) {
          console.error('Error updating search index:', indexError);
        }
      }

      navigate('/admin/posts', { replace: true });
    } catch (err) {
      console.error('Error saving post:', err);
      setError('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setFormData(prev => ({
      ...prev,
      title: newTitle,
      slug: !prev.slug || prev.slug === generateSlug(prev.title) ? generateSlug(newTitle) : prev.slug
    }));
  };

  const handleTitleBlur = () => {
    if (formData.title && !formData.slug) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(prev.title)
      }));
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setFormData(prev => ({
      ...prev,
      content: newContent
    }));
  };

  const handleContentBlur = () => {
    if (formData.content && !formData.excerpt) {
      setFormData(prev => ({
        ...prev,
        excerpt: generateExcerpt(prev.content)
      }));
    }
  };

  const handleImageSelect = (url: string, imageHtml?: string) => {
    if (imageHtml) {
      if (contentCursorPosition !== null) {
        const content = formData.content;
        const newContent = content.slice(0, contentCursorPosition) + 
                        imageHtml + 
                        content.slice(contentCursorPosition);
        setFormData({ ...formData, content: newContent });
      }
    } else {
      setFormData(prev => ({ ...prev, image_url: url }));
    }
    setShowImageSelector(false);
  };

  const handleContentVideoSelect = (videoHtml: string) => {
    if (contentCursorPosition !== null) {
      const content = formData.content;
      const newContent = content.slice(0, contentCursorPosition) + 
                      videoHtml + 
                      content.slice(contentCursorPosition);
      
      setFormData({ ...formData, content: newContent });
    }
  };

  const handleDownloadSelect = (downloadHtml: string) => {
    if (contentCursorPosition !== null) {
      const content = formData.content;
      const newContent = content.slice(0, contentCursorPosition) + 
                      downloadHtml + 
                      content.slice(contentCursorPosition);
      
      setFormData({ ...formData, content: newContent });
    }
    setShowDownloadSelector(false);
  };

  const handleLabExerciseSelect = (exerciseId: string) => {
    setFormData({ ...formData, lab_exercise_id: exerciseId });
    setShowLabExerciseSelector(false);
  };

  const handleContentClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    const textarea = e.target as HTMLTextAreaElement;
    setContentCursorPosition(textarea.selectionStart);
  };

  const handlePublishDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      published_at: newDate
    }));
  };

  const handleCalendarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get the date input element
    const dateInput = document.getElementById('published_date');
    if (!dateInput) return;

    try {
      // Try to use the showPicker API first
      if ('showPicker' in HTMLInputElement.prototype) {
        (dateInput as any).showPicker();
      } else {
        // Fallback to click() if showPicker is not available
        dateInput.click();
      }
    } catch (error) {
      // If showPicker fails (e.g., in iframe), fallback to click()
      dateInput.click();
    }
  };

  const handleAIContentSelect = (content: string) => {
    setFormData(prev => ({
      ...prev,
      content: content,
      excerpt: !prev.excerpt ? generateExcerpt(content) : prev.excerpt
    }));
  };

  const handleAITitleSelect = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title: title,
      slug: !prev.slug ? generateSlug(title) : prev.slug
    }));
  };

  const handleAIMetaSelect = (meta: string) => {
    setFormData(prev => ({
      ...prev,
      excerpt: meta
    }));
  };

  const handlePublishChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isPublished = e.target.checked;
    
    // If turning on publish and no publish date, set to today
    if (isPublished && !formData.published_at) {
      const today = new Date().toISOString().split('T')[0];
      setFormData({ 
        ...formData, 
        is_published: isPublished,
        published_at: today
      });
    } else {
      setFormData({ 
        ...formData, 
        is_published: isPublished 
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={() => navigate('/admin/posts', { replace: true })}
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Posts
        </button>
        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={() => setShowAIModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Bot className="h-5 w-5 mr-2" />
            AI Assistant
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? 'Saving...' : 'Save Post'}
          </button>
        </div>
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={handleTitleChange}
                onBlur={handleTitleBlur}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Slug
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Content
              </label>
              <div className="mt-1 relative">
                <div className="absolute top-2 right-2 flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowLabExerciseSelector(true)}
                    className="p-2 rounded hover:bg-gray-100"
                    title="Add lab exercise"
                  >
                    <FlaskConical className="h-5 w-5 text-gray-500" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowImageSelector(true)}
                    className="p-2 rounded hover:bg-gray-100"
                    title="Insert image"
                  >
                    <ImageIcon className="h-5 w-5 text-gray-500" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowVideoSelector(true)}
                    className="p-2 rounded hover:bg-gray-100"
                    title="Insert video"
                  >
                    <Video className="h-5 w-5 text-gray-500" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDownloadSelector(true)}
                    className="p-2 rounded hover:bg-gray-100"
                    title="Insert download section"
                  >
                    <Download className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
                <textarea
                  rows={20}
                  value={formData.content}
                  onChange={handleContentChange}
                  onBlur={handleContentBlur}
                  onClick={handleContentClick}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Lab Exercise
              </label>
              <select
                value={formData.lab_exercise_id || ''}
                onChange={(e) => setFormData({ ...formData, lab_exercise_id: e.target.value || null })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
              >
                <option value="">No Lab Exercise</option>
                {labExercises.map((exercise) => (
                  <option key={exercise.id} value={exercise.id}>
                    {exercise.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Excerpt
              </label>
              <textarea
                rows={3}
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Featured Image
              </label>
              <div className="flex items-start space-x-4">
                <div className="flex-grow">
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="Image URL"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowImageSelector(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ImageIcon className="h-5 w-5 mr-2" />
                  Browse
                </button>
              </div>
              {formData.image_url && (
                <div className="mt-2">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="h-32 w-auto object-cover rounded-lg"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Author
              </label>
              <select
                value={formData.author_id}
                onChange={(e) => setFormData({ ...formData, author_id: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              >
                <option value="">Select an author</option>
                {authors.map((author) => (
                  <option key={author.id} value={author.id}>
                    {author.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <select
                value={formData.category_id}
                onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                required
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                CTA Type
              </label>
              <select
                value={formData.cta_type || ''}
                onChange={(e) => setFormData({ ...formData, cta_type: e.target.value as typeof formData.cta_type })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
              >
                <option value="">No CTA</option>
                {ctaTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Published Date
              </label>
              <div className="mt-1 relative">
                <button
                  type="button"
                  onClick={handleCalendarClick}
                  className="absolute inset-y-0 left-0 pl-3 flex items-center"
                >
                  <Calendar className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                </button>
                <input
                  id="published_date"
                  type="date"
                  value={formData.published_at || ''}
                  onChange={handlePublishDateChange}
                  className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D] focus:ring-opacity-50"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={handlePublishChange}
                className="h-4 w-4 text-[#F98B3D] focus:ring-[#F98B3D] border-gray-300 rounded"
              />
              <label htmlFor="is_published" className="ml-2 block text-sm text-gray-900">
                Publish post
              </label>
            </div>
          </div>
        </div>
      </div>

      <ImageSelectorModal
        isOpen={showImageSelector}
        onClose={() => setShowImageSelector(false)}
        onSelect={handleImageSelect}
        mode={contentCursorPosition !== null ? 'content' : 'featured'}
      />

      <VideoSelectorModal
        isOpen={showVideoSelector}
        onClose={() => setShowVideoSelector(false)}
        onSelect={handleContentVideoSelect}
      />

      <DownloadSelectorModal
        isOpen={showDownloadSelector}
        onClose={() => setShowDownloadSelector(false)}
        onSelect={handleDownloadSelect}
      />

      <LabExerciseSelectorModal
        isOpen={showLabExerciseSelector}
        onClose={() => setShowLabExerciseSelector(false)}
        onSelect={handleLabExerciseSelect}
        selectedExerciseId={formData.lab_exercise_id}
      />

      <BlogAIModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onContentSelect={handleAIContentSelect}
        onTitleSelect={handleAITitleSelect}
        onMetaSelect={handleAIMetaSelect}
      />
    </form>
  );
}